/**
 * Tour de bienvenida a la aplicación
 * Este tour guía a los usuarios mostrándoles las funcionalidades básicas
 * y cómo acceder a los tutoriales de cada proceso.
 */

import { Tour, TourStep } from '../models/interfaces'
import { saveTourState } from '../utils/helpers'
import {
  navigateAndWait,
  wait,
  toggleMiniDrawer,
  openTicketDialog,
  closeTicketDialog,
  finishWelcomeTour,
  scrollToElement,
  findFirstAvailableElement,
  aggressiveScrollToTop,
  customEvent,
} from '../utils/tourHelpers'
import router from '@/router'

/**
 * Tour de bienvenida para usuarios nuevos
 * No depende del rol de usuario - se muestra a todos
 */
export const WelcomeTour: Tour = {
  id: 'welcome-tour',
  name: 'Tour de Bienvenida a GENIO',
  steps: [
    // Paso 1: Bienvenida general (Home.vue)
    {
      target: 'body', // Usar body como fallback seguro
      title: '¡Bienvenido!',
      content: 'Te guiare a través de mis principales funcionalidades.',
      placement: 'center',
      beforeStep: async () => {
        // Asegurarse de que estamos en Home
        if (router.currentRoute.name !== 'home') {
          await navigateAndWait('home')
        }
      },
    },

    // Paso 2: Explicación del menú circular
    {
      target: '.circular-image-container',
      title: 'Home',
      content: 'Aquí encontraras los tutoriales de cada proceso.',
      placement: 'bottom',
      highlightPadding: 6,
    },

    // Paso 3: Explicación de los iconos de proceso
    {
      target: '.icon-circle',
      title: 'Procesos',
      content: `Cada opción representa un de los procesos que usaras a diario. 

      Haz click en cualquiera para acceder a tutoriales específicos de cada paso`,
      placement: 'bottom',
      highlightPadding: 5,
      afterStep: async () => {
        toggleMiniDrawer(true)
      },
    },

    // Paso 4: Guiar al usuario hacia los tutoriales
    {
      target: '.button-help',
      title: 'Acceso directo',
      content: 'Desde el botón de ayuda te llevare directamente a los tutoriales.',
      placement: 'top',
      highlightPadding: 5,
      afterStep: async () => {
        await navigateAndWait('help')
        toggleMiniDrawer(false)
      },
    },

    // Paso 5: Explicación de la página de ayuda (HelpPage.vue)
    {
      target: '.help-container',
      title: 'Centro de Ayuda',
      content: 'Encuentra aqui los tutoriales organizados por proceso.',
      placement: 'top',
      highlightPadding: 6,
      scrollIntoView: false,
      beforeStep: async () => {
        // Asegurarse de que estamos en la página de ayuda
        if (!router.currentRoute.path.includes('help')) {
          await navigateAndWait('help', 3000)
        }

        // Primero hacer scroll al inicio de la página
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Esperar un poco más y luego hacer scroll al elemento específico
        setTimeout(() => {
          scrollToElement('.help-container')
        }, 3000)
      },
    },

    // Paso 6: Explicación del buscador
    {
      target: '.header', // Seleccionar un contenedor que incluya tanto la barra como las pestañas
      title: 'Buscador y Categorías',
      content: 'Usa el buscador para encontrar tutoriales específicos o navega por las pestañas de las diferentes categorías de procesos.',
      placement: 'bottom',
      highlightPadding: 20, // Aumentamos el padding para cubrir más área
      beforeStep: async () => {
        // Asegurar que la vista está posicionada correctamente
        window.scrollTo({ top: 100, behavior: 'smooth' })

        // Esperar para que el scroll se complete
        await wait(500)

        // Intentar encontrar el elemento y hacer scroll si es necesario
        scrollToElement('.search-bar')
      },
    },

    // Paso 7: Explicación de los videos
    {
      target: '.videos-container .title, .videos-container h2, .videos-container > *:first-child',
      title: 'Tutoriales en Video',
      content: 'Explora, revisa y visualiza videos instructivos sobra cada funcionalidad.',
      placement: 'top',
      highlightPadding: 15,
      scrollIntoView: true,
      // Asegurar que el contenedor de videos esté visible
      beforeStep: async () => {
        // Método agresivo para forzar scroll al inicio
        await aggressiveScrollToTop()

        // Buscar elementos dentro del contenedor para apuntar más específicamente
        const targetSelectors = ['.videos-container .title', '.videos-container h2', '.videos-container > *:first-child']
        const foundTarget = findFirstAvailableElement(targetSelectors)

        if (!foundTarget) {
          console.error('No se encontró elemento específico, usando contenedor general')
        }
        // Esperar a que se complete la operación
        await wait(1000)
      },
    },
    // Paso 8: Explicación de tickets y requerimientos
    {
      target: '.requirement-tickets',
      title: 'Mejoras y fallos',
      content: 'Con sólo pinchar en mi logo, podrás enviarme aviso de error, tus ideas de mejoras o nuevas funcionalidades.',
      placement: 'bottom',
      highlightPadding: 6,
      beforeStep: async () => {
        toggleMiniDrawer(true)
      },
      afterStep: async () => {
        toggleMiniDrawer(false)
        await wait(1000)
        openTicketDialog()
        await wait(1000)
      },
    },
    // Paso 9: Explicación del formulario de tickets
    {
      target: '.ticket-dialog',
      title: 'Formulario de Requerimientos',
      content: 'En este formulario puedes seleccionar el tipo de reporte (Fallo o Mejora), escribir una descripción detallada y adjuntar imágenes o videos que ayuden a entender mejor tu solicitud.',
      placement: 'bottom',
      highlightPadding: 30,
    },
    {
      target: '.requirement-tutorial',
      title: 'Requerimientos',
      content: 'Aquí podrás ver los requerimientos que has enviado y hacerle seguimiento con el ID de requerimiento asociado o titulo.',
      placement: 'bottom',
      highlightPadding: 5,
      beforeStep: async () => {
        toggleMiniDrawer(true)
        closeTicketDialog()
        await wait(1000)
        if (!router.currentRoute.path.includes('requirements')) {
          await navigateAndWait('requirements')
        }
      },
      afterStep: async () => {
        toggleMiniDrawer(false)
      },
    },
    {
      target: '.tabs-tutorial',
      title: 'Botones de estado',
      content: `Cada botón representa el estado de los requerimientos. 
      
      Haz click en cualquiera para ver los requerimientos en ese estado.`,
      placement: 'bottom',
      highlightPadding: 6,
    },
    {
      target: '.data-table-ticket',
      title: 'Tabla de Requerimientos',
      content: 'En esta tabla podrás ver los requerimientos que has enviado y hacerle seguimiento.',
      placement: 'bottom',
      highlightPadding: 6,
      afterStep: async () => {
        toggleMiniDrawer(true)
        await wait(1000)
      },
    },
    {
      target: '.schedule-tutorial',
      title: 'Agenda',
      content: 'En esta sección podrás ver todo lo agendado.',
      placement: 'bottom',
      highlightPadding: 6,
      beforeStep: async () => {
        await navigateAndWait('schedule')
      },
      afterStep: async () => {
        toggleMiniDrawer(false)
      },
    },
    {
      target: '.schedule-tabs-tutorial',
      title: 'Tabs de Agenda',
      content: 'En esta sección podrás filtrar los eventos por proceso.',
      placement: 'bottom',
      highlightPadding: 6,
    },
    {
      target: '.schedule-chip-group-tutorial',
      title: 'Vistas de la agenda',
      content: 'Aquí podrás modificar la vista de la agenda.',
      placement: 'bottom',
      highlightPadding: 6,
    },
    {
      target: '.schedule-calendar-tutorial',
      title: 'Calendario',
      content: 'Aquí visualizaras los eventos agendados inclusive los eventos del día actual y al darle click podrás ver los detalles.',
      placement: 'top',
      highlightPadding: 6,
      afterStep: async () => {
        await aggressiveScrollToTop()
        window.scrollTo({ top: 0, behavior: 'smooth' })
        scrollToElement('.requirement-tickets')
        await wait(500)
        toggleMiniDrawer(true)
        await wait(500)
      },
    },
    {
      target: '.notification-system-tutorial',
      title: 'Notificaciones',
      content: 'Aquí podrás ver las notificaciones de los procesos.',
      placement: 'top',
      highlightPadding: 6,
    },
    {
      target: '.whatsapp-btn-tutorial-row .whatsapp-btn-tutorial',
      title: 'Whatsapp',
      content: 'Aquí podrás contactar al equipo de soporte en caso de un problema que te impida continuar con algún proceso',
      placement: 'top',
      highlightPadding: 6,
    },
    {
      target: '.user-avatar',
      title: 'Usuario',
      content: 'Aquí podrás ver otras opciones al hacer click en tus iniciales.',
      placement: 'top',
      highlightPadding: 6,
      afterStep: async () => {
        customEvent('open-perfil-menu')
        customEvent('open-drawer', true)
        await wait(500)
      },
    },
    {
      target: '.active-user-profile-tutorial',
      title: 'Perfil',
      content: 'Aquí podrás ver tus datos personales y actualizarlos.',
      placement: 'top',
      highlightPadding: 6,
      beforeStep: async () => {
        await wait(500)
      },
    },
    {
      target: '.active-user-speed-test-tutorial',
      title: 'Test de velocidad',
      content: 'Aquí podrás medir la velocidad de tu internet.',
      placement: 'top',
      highlightPadding: 6,
    },
    {
      target: '.active-user-logout-tutorial',
      title: 'Cerrar sesión',
      content: 'Aquí podrás cerrar sesión.',
      placement: 'top',
      highlightPadding: 6,
      afterStep: async () => {
        customEvent('open-perfil-menu', false)
        customEvent('open-drawer', false)
        toggleMiniDrawer(false)
        localStorage.setItem('tour-just-finished', 'true')
        await router.push({ name: 'home' })
      },
    },
  ],
  // Función que se ejecuta al completar el tour
  onComplete: () => {
    saveTourState('welcome-tour', true)

    // Marcar que acabamos de terminar el tour para evitar reinicio automático
    localStorage.setItem('tour-just-finished', 'true')

    // Emitir evento de finalización
    finishWelcomeTour(false)
  },
  // Función que se ejecuta si el usuario omite el tour
  onSkip: () => {
    saveTourState('welcome-tour', false)

    // Guardar en localStorage que el usuario omitió el tour
    localStorage.setItem('welcome-tour-skipped', 'true')

    // Marcar que acabamos de terminar el tour para evitar reinicio automático
    localStorage.setItem('tour-just-finished', 'true')

    // Emitir evento de finalización con indicador de omitido
    finishWelcomeTour(true)
  },
}
